import { useState } from 'react';
import { notification } from 'components/ds';
import { CreatePasswordRequest, RecoveryPasswordRequest } from '../_types/password.types';
import { passwordService } from '../services/password.service';
import { useAuthStorage } from './useAuthStorage';

interface UsePasswordReturn {
  recoveryPassword: (params: RecoveryPasswordRequest) => Promise<boolean>;
  createPassword: (data: CreatePasswordRequest) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

export const usePassword = (): UsePasswordReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {accessToken} = useAuthStorage();
  
  const createPassword = async (data: CreatePasswordRequest): Promise<boolean> => {
    if (isLoading) return false;
    
    try {
      setIsLoading(true);
      setError(null);
      const response = await passwordService.createPassword(data, accessToken);
      
      if (response.success) {
        notification.success({
          message: 'Senha criada',
          description: 'Sua senha foi criada com sucesso!'
        });
        
        return true;
      }
      
      notification.error({
        message: 'Erro ao criar senha',
        description: response.message || 'Não foi possível criar a senha. Tente novamente.'
      });
      
      return false;
      
    } catch (err: any) {
      console.error('Erro ao criar senha:', err);
      
      // Extract error description from the response structure
      const errorDescription = err?.response?.data?.errors?.[0]?.description || 'Erro ao criar senha';
      setError(errorDescription);
      
      notification.error({
        message: 'Erro ao criar senha',
        description: errorDescription
      });
      
      return false;
      
    } finally {
      setIsLoading(false);
    }
  };
  
  const recoveryPassword = async (params: RecoveryPasswordRequest): Promise<boolean> => {
    if (isLoading) return false;
    
    try {
      setIsLoading(true);
      setError(null);
      
      await passwordService.recoveryPassword(params, accessToken);
      
      notification.success({
        message: 'Senha alterada com sucesso',
        description: 'Você já pode fazer login com sua nova senha.'
      });
      
      return true;
      
    } catch (err: any) {
      console.error('Erro ao redefinir senha:', err);
      
      // Extract error description from the response structure
      const errorDescription = err?.response?.data?.errors?.[0]?.description || 'Não foi possível definir sua senha';
      setError(errorDescription);
      
      notification.error({
        message: 'Erro ao redefinir senha',
        description: errorDescription
      });
      
      return false;
      
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    recoveryPassword,
    createPassword,
    isLoading,
    error
  };
};
